import React, { useState } from "react";
import { BadgeCheck, ThumbsUp, ThumbsDown, Flag, Clock, Share2 } from "lucide-react";
import UserLevelBadge from "@/components/site/UserLevelBadge";
import FoundingParentBadge from "@/components/site/FoundingParentBadge";
import NpsRuler from "@/components/site/NpsRuler"; // eslint-disable-line no-unused-vars
import ReviewCompositeScore from "@/components/site/schooldetail/ReviewCompositeScore";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { toast } from "sonner";

/**
 * ExpandableText — preserves paragraph breaks (whitespace-pre-wrap) AND caps
 * long text at ~400 chars OR 5 lines with a "baca selengkapnya" link.
 *
 * Truncation policy:
 *  - Character cap `charLimit` (default 400) is applied to `text.length`
 *    → cheap, fast, works for reviews with no paragraph breaks.
 *  - Paragraph cap: also collapse to `charLimit` chars when the text has
 *    more paragraphs than `lineLimit` (default 5) so short-line reviews get
 *    truncated too.
 *  - When collapsed, we slice at a word boundary to avoid mid-word cuts,
 *    then trim trailing punctuation and append "…".
 */
export function ExpandableText({ text, testid, charLimit = 400, lineLimit = 5, className = "" }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const paraCount = text.split(/\n{2,}/).length;
  const needsTruncate = text.length > charLimit || paraCount > lineLimit;
  if (!needsTruncate || expanded) {
    return (
      <p className={`whitespace-pre-wrap ${className}`} data-testid={testid}>
        {text}
        {expanded && needsTruncate && (
          <>
            {" "}
            <button
              type="button"
              onClick={() => setExpanded(false)}
              data-testid={testid ? `${testid}-collapse` : undefined}
              className="text-[#BE5730] hover:text-[#D2673E] font-medium underline underline-offset-2 transition-colors"
            >
              Tampilkan lebih sedikit
            </button>
          </>
        )}
      </p>
    );
  }
  // Cut at word boundary near charLimit, strip trailing punctuation, append …
  let slice = text.slice(0, charLimit);
  const lastSpace = slice.lastIndexOf(" ");
  if (lastSpace > charLimit * 0.6) slice = slice.slice(0, lastSpace);
  slice = slice.replace(/[.,;:!?\-—\s]+$/u, "");
  return (
    <p className={`whitespace-pre-wrap ${className}`} data-testid={testid}>
      {slice}…{" "}
      <button
        type="button"
        onClick={() => setExpanded(true)}
        data-testid={testid ? `${testid}-expand` : undefined}
        className="text-[#BE5730] hover:text-[#D2673E] font-medium underline underline-offset-2 transition-colors"
      >
        baca selengkapnya
      </button>
    </p>
  );
}

const WA_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" width="16" height="16" fill="currentColor" aria-hidden="true">
    <path d="M19.11 17.28c-.28-.14-1.65-.81-1.9-.9-.26-.09-.44-.14-.63.14-.18.28-.72.9-.88 1.09-.16.18-.32.21-.6.07-.28-.14-1.17-.43-2.23-1.37-.83-.74-1.38-1.65-1.55-1.94-.16-.28-.02-.43.12-.57.13-.13.28-.32.42-.49.14-.16.19-.28.28-.46.09-.19.05-.35-.02-.49-.07-.14-.63-1.52-.86-2.08-.23-.55-.46-.48-.63-.49H10c-.19 0-.49.07-.75.35-.26.28-.98.96-.98 2.34s1 2.72 1.14 2.9c.14.19 1.97 3.01 4.78 4.22 1.19.51 2.16.68 2.9.87.66.17 1.26.14 1.73.09.53-.06 1.65-.68 1.89-1.34.23-.66.23-1.22.17-1.34-.07-.13-.26-.21-.54-.35zM16 3.2C8.94 3.2 3.2 8.94 3.2 16c0 2.26.6 4.47 1.74 6.42L3 29l6.75-1.86A12.7 12.7 0 0 0 16 28.8C23.06 28.8 28.8 23.06 28.8 16S23.06 3.2 16 3.2zm0 23.36a10.5 10.5 0 0 1-5.35-1.47l-.38-.23-4 1.1 1.07-3.9-.25-.4A10.55 10.55 0 1 1 26.56 16c0 5.83-4.73 10.56-10.56 10.56z" />
  </svg>
);

const THREADS_ICON = (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 192 192" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M141.537 88.988a66.667 66.667 0 0 0-2.518-1.143c-1.482-27.307-16.403-42.94-41.457-43.1h-.34c-14.986 0-27.449 6.396-35.12 18.036l13.779 9.452c5.73-8.695 14.724-10.548 21.348-10.548h.229c8.249.053 14.474 2.452 18.503 7.129 2.932 3.405 4.892 8.111 5.864 14.05-7.32-1.243-15.235-1.626-23.702-1.14-23.86 1.375-39.198 15.29-38.168 34.629.522 9.811 5.407 18.253 13.752 23.768 7.052 4.66 16.134 6.939 25.573 6.424 12.466-.685 22.244-5.44 29.06-14.14 5.174-6.607 8.446-15.171 9.879-25.978 5.878 3.548 10.234 8.221 12.64 13.836 4.089 9.545 4.326 25.235-8.466 38.017-11.213 11.202-24.696 16.048-45.087 16.198-22.626-.168-39.732-7.42-50.845-21.55-10.404-13.229-15.78-32.35-15.978-56.827.198-24.477 5.574-43.598 15.978-56.827 11.113-14.13 28.219-21.382 50.845-21.55 22.786.169 40.19 7.454 51.741 21.646 5.662 6.96 9.934 15.71 12.755 25.913l15.518-4.156c-3.424-12.552-8.809-23.377-16.116-32.362-14.812-18.204-36.475-27.532-64.398-27.727h-.108c-27.867.195-49.28 9.56-63.634 27.831C11.397 41.048 5.10 61.628 4.884 88.51l-.001.985.001.999c.215 26.88 6.512 47.462 18.72 61.19 14.354 18.271 35.767 27.637 63.634 27.831h.108c24.771-.172 42.229-6.65 56.6-21.007 18.802-18.788 18.238-42.323 12.038-56.777-4.446-10.37-12.918-18.793-24.487-24.744ZM98.44 129.507c-10.44.588-21.286-4.098-21.82-14.135-.397-7.442 5.301-15.746 22.461-16.735 1.966-.113 3.895-.169 5.79-.169 6.235 0 12.068.606 17.373 1.765-1.978 24.702-13.58 28.713-23.804 29.274Z" />
  </svg>
);

/**
 * Single review card used in SchoolDetail /schools/{id} → Review tab.
 * Presentational component; parent owns state (photos, user auth, report dialog).
 */
export default function ReviewCard({ r, i, photos = [], user, onReport, onLoginRequired, schoolName, highlighted = false }) {
  const attachedPhotos = (r.photo_ids || []).length
    ? photos.filter((p) => r.photo_ids.includes(p.photo_id))
    : [];

  // Avatar: circle initial (design rule — every review card has a visual anchor).
  // Anonymous reviews use "A" (Anonim); others use first letter of the display name.
  const displayName = r.display_name || r.user_name || "Anonim";
  const avatarInitial = (displayName || "?").trim().charAt(0).toUpperCase();

  // Role label for the single-line meta row (design rule: coral is for accents,
  // not meta text — meta uses --text-3 muted gray).
  const roleLabel = r.reviewer_role === "student"
    ? "Siswa"
    : r.reviewer_role === "alumni"
      ? "Alumni"
      : "Wali Murid";

  // Share text — inviting CTA that mentions the school and headline.
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}${window.location.pathname}#review-${r.review_id}`
    : "";
  const shareText = `📖 Review menarik tentang ${schoolName || "sekolah ini"} di The School Reports: "${(r.title || "").slice(0, 120)}"\n\nBaca selengkapnya:`;
  const waHref = `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`;
  const threadsHref = `https://www.threads.net/intent/post?text=${encodeURIComponent(shareText + "\n" + shareUrl)}`;
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success("Tautan review disalin"); }
    catch { toast.error("Gagal menyalin tautan"); }
  };

  return (
    <div
      id={`review-${r.review_id}`}
      data-testid={`review-item-${i}`}
      className={
        "bg-white border rounded-2xl p-5 transition-all duration-500 scroll-mt-24 " +
        (highlighted
          ? "border-[#D2673E] ring-4 ring-[#D2673E]/25 shadow-[0_0_0_1px_#D2673E]"
          : "border-[#EAE7E0]")
      }
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* Avatar — circle initial (design rule: coral tint bg, coral text) */}
          <div
            data-testid={`review-avatar-${i}`}
            className="flex-shrink-0 w-10 h-10 rounded-full bg-[#FDF2EF] text-[#D2673E] flex items-center justify-center font-bold text-base tabular-nums select-none"
            aria-label={`Avatar ${displayName}`}
          >
            {avatarInitial}
          </div>
          <div className="min-w-0">
            <div className="text-sm text-[#1F2937] inline-flex items-center gap-2 flex-wrap">
              {/* Name — Plus Jakarta Sans 700, navy-ink, NEVER italic (design rule: serif only ≥28px) */}
              <span data-testid={`review-author-${i}`} className="font-bold text-[#1F2937]">
                {displayName}
              </span>
              {(r.reviewer_role === "student" || r.reviewer_role === "alumni") && (
                <span data-testid={`review-role-${i}`} className={
                  "inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border " +
                  (r.reviewer_role === "student"
                    ? "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]"
                    : "bg-[#FEF3E7] text-[#7C2D12] border-[#FED7AA]")
                }>
                  {r.reviewer_role === "student" ? "Siswa Aktif" : `Alumni · ${r.graduation_year || "—"}`}
                </span>
              )}
              {r.verified_parent && (
                <span data-testid={`review-verified-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534] text-[10px] uppercase tracking-wider border border-[#BBF7D0]"><BadgeCheck size={10} /> Terverifikasi</span>
              )}
              {!r.is_anonymous && <UserLevelBadge name={r.user_level_name} order={r.user_level_order} testid={`review-user-level-${i}`} />}
              {!r.is_anonymous && r.is_founding_parent && <FoundingParentBadge testid={`review-founding-${i}`} />}
            </div>
            {/* Single-line meta row — muted gray (--text-3), dot separators (·).
                Example: "Wali Murid · Angkatan 2026/2027 · 5/8/2026" */}
            <div
              className="mt-0.5 text-xs text-[#4B5563] flex items-center flex-wrap gap-x-1.5"
              data-testid={`review-meta-${i}`}
            >
              <span>{roleLabel}</span>
              {r.attended_academic_year && (r.reviewer_role === "parent" || r.reviewer_role === "student" || !r.reviewer_role) && (
                <>
                  <span aria-hidden>·</span>
                  <span data-testid={`review-attended-year-${i}`}>Angkatan {r.attended_academic_year}</span>
                </>
              )}
              <span aria-hidden>·</span>
              <span>{new Date(r.created_at).toLocaleDateString("id-ID")}</span>
              {r.verified_parent && r.child_grade && (
                <>
                  <span aria-hidden>·</span>
                  <span>Wali murid {r.child_grade}</span>
                </>
              )}
              {r.edit_count > 0 && (
                <>
                  <span aria-hidden>·</span>
                  <span data-testid={`review-edit-history-${i}`} className="italic">Diedit {r.edit_count}×</span>
                </>
              )}
            </div>
          </div>
        </div>
        <ReviewCompositeScore ratings={r.ratings} testid={`review-composite-${i}`} />
      </div>

      {/* Spec v1.1 §3 — TITLE IS HIDDEN. The stored `title` remains in the DB
          (for admin moderation lists) but never renders on the public card;
          the body itself is the story. Line-clamp comes from ExpandableText. */}
      {/* Review body with paragraph support + "baca selengkapnya" expansion.
          `whitespace-pre-wrap` preserves Enter presses from the reviewer's textarea. */}
      <ExpandableText
        text={r.body}
        testid={`review-body-${i}`}
        charLimit={400}
        lineLimit={4}
        className="mt-2 text-[15px] text-[#374151] leading-[1.75] tracking-[0.005em] max-w-[68ch]"
      />

      {(r.pros || r.cons) && (
        <div className="mt-3 grid sm:grid-cols-2 gap-2" data-testid={`review-proscons-${i}`}>
          {r.pros && (
            <div className="p-3 rounded-xl bg-[#F0FDF4] border border-[#BBF7D0]">
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#166534] font-semibold"><ThumbsUp size={10} /> Kelebihan</div>
              <ExpandableText
                text={r.pros}
                testid={`review-pros-${i}`}
                charLimit={300}
                lineLimit={4}
                className="mt-1 text-sm text-[#1F2937] leading-snug"
              />
            </div>
          )}
          {r.cons && (
            <div className="p-3 rounded-xl bg-[#FDF2EF] border border-[#FBCFC0]">
              <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#9F3B24] font-semibold"><ThumbsDown size={10} /> Perlu Ditingkatkan</div>
              <ExpandableText
                text={r.cons}
                testid={`review-cons-${i}`}
                charLimit={300}
                lineLimit={4}
                className="mt-1 text-sm text-[#1F2937] leading-snug"
              />
            </div>
          )}
        </div>
      )}

      {attachedPhotos.length > 0 && (
        <div className="mt-3 grid grid-cols-3 gap-2" data-testid={`review-photos-${i}`}>
          {attachedPhotos.slice(0, 6).map((p) => (
            <img key={p.photo_id} src={p.data_url} alt="foto review" className="w-full aspect-square object-cover rounded-lg border border-[#EAE7E0]" />
          ))}
        </div>
      )}

      {(() => {
        // Spec v1.1 §1C — Rekomendasi lives as a TOP-LEVEL field now; the legacy
        // `ratings.nps` is a fallback for old rows we haven't run migration against yet.
        const reko = r.rekomendasi ?? r.ratings?.nps;
        if (typeof reko !== "number") return null;
        return (
          <div className="mt-3 p-3 rounded-xl bg-[#F9F8F6] border border-[#EAE7E0]" data-testid={`review-nps-block-${i}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#4B5563] font-semibold mb-1">Seberapa Layak Direkomendasikan</div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-[#EAE7E0] rounded-full overflow-hidden">
                <div
                  className={"h-full rounded-full " + (reko >= 9 ? "bg-[#166534]" : reko >= 7 ? "bg-[#CA8A04]" : "bg-[#B91C1C]")}
                  style={{ width: `${Math.max(0, Math.min(100, reko * 10))}%` }}
                />
              </div>
              <span
                data-testid={`review-nps-ruler-${i}`}
                className={"font-serif-display text-lg tabular-nums " + (reko >= 9 ? "text-[#166534]" : reko >= 7 ? "text-[#CA8A04]" : "text-[#B91C1C]")}
              >
                {reko}/10
              </span>
            </div>
          </div>
        );
      })()}

      {/* Footer */}
      <div className="mt-3 pt-3 border-t border-[#EAE7E0] flex items-center justify-between gap-2 flex-wrap">
        {r.is_own && !r.is_verified ? (
          <div data-testid={`review-verifying-${i}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#92400E] bg-[#FFFBEB] border border-[#FDE68A] rounded-full px-2.5 py-1">
            <Clock size={11} /> Sedang diverifikasi tim
          </div>
        ) : r.is_verified ? (
          <div data-testid={`review-team-verified-${i}`} className="inline-flex items-center gap-1.5 text-[11px] text-[#065F46] bg-[#ECFDF5] border border-[#A7F3D0] rounded-full px-2.5 py-1">
            <BadgeCheck size={11} /> Diverifikasi tim
          </div>
        ) : <span />}
        <div className="flex items-center gap-3">
          {/* Share popover */}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`review-share-btn-${i}`}
                className="inline-flex items-center gap-1 text-[11px] text-[#4B5563] hover:text-[#D2673E] transition-colors"
                title="Bagikan review ini"
              >
                <Share2 size={11} /> Bagikan
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" side="top" className="w-60 rounded-2xl border border-[#EAE7E0] p-2" data-testid={`review-share-popover-${i}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#4B5563] px-3 py-1.5">Bagikan review</div>
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid={`review-share-wa-${i}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#25D366] hover:bg-[#F0FDF4] text-sm transition-colors"
              >
                {WA_ICON} <span className="text-[#1F2937]">WhatsApp</span>
              </a>
              <a
                href={threadsHref}
                target="_blank"
                rel="noreferrer"
                data-testid={`review-share-threads-${i}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-black hover:bg-[#F3F1ED] text-sm transition-colors"
              >
                {THREADS_ICON} <span className="text-[#1F2937]">Threads</span>
              </a>
              <button
                onClick={copyLink}
                data-testid={`review-share-copy-${i}`}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-[#4B5563] hover:bg-[#F3F1ED] text-sm transition-colors w-full text-left"
              >
                <Share2 size={13} /> <span className="text-[#1F2937]">Salin tautan</span>
              </button>
            </PopoverContent>
          </Popover>
          {!r.is_own && (
            <button type="button" onClick={() => (user ? onReport?.(r) : onLoginRequired?.())}
              data-testid={`review-report-btn-${i}`}
              className="inline-flex items-center gap-1 text-[11px] text-[#4B5563] hover:text-[#B91C1C] transition-colors"
              title="Laporkan review">
              <Flag size={11} /> Laporkan
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
